/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import { useUser } from '../hooks/UNSAFE_useUser';
import { useEffect, useRef, useState } from 'preact/hooks';
import { mergeProps } from '../utils/UNSAFE_mergeProps';
import { classNames } from '../utils/UNSAFE_classNames';
import { CircleWrapper, CircleInner } from '../PRIVATE_Meter';
import { CircularReferenceLine } from './CircularReferenceLine';

import {
  validateRange,
  getTrackAndIndicatorColor,
  getMeterAriaProps,
  getCircleSection
} from '../utils/PRIVATE_meterUtils';

import { getDimensions, getValue, getCenterContext } from './CircleMeterUtils';

import {
  getIndicatorProps,
  getTrackProps,
  getThresholdsProps,
  getReferenceLinesProps,
  MeterInfo
} from './LayoutUtils';

import {
  usePointerEvents,
  useKeyboardEvents,
  useMeterDatatip
} from '../hooks/PRIVATE_useDvtMeterEvents';

import { MeterProps, CircularRefLine } from './MeterCircle.types';
import { useTabbableMode } from '../hooks/UNSAFE_useTabbableMode';
import { useComponentTheme } from '../hooks/UNSAFE_useComponentTheme';
import { MeterCircleStyles, MeterCircleVariantOptions } from './themes/MeterCircleStyles.css';
import { MeterCircleRedwoodTheme } from './themes/redwood/MeterCircleTheme';
import { useTestId } from '../hooks/UNSAFE_useTestId';

/**
 * A Meter circle displays information graphically in a circular bar, highlighting a specific metric value's progress in relation to its min, max, or thresholds.
 */
export function MeterCircle({
  max = 100,
  min = 0,
  value = 0,
  step = 1,
  size,
  startAngle = 90,
  angleExtent = 360,
  isTrackRendered = true,
  thresholdDisplay = 'all',
  indicatorSize = 1,
  testId,
  ...props
}: MeterProps) {
  const testIdProps = useTestId(testId);

  validateRange(min, max, value, step);
  const currentInputRef = useRef<number>();
  const { direction } = useUser();
  const isRtl = direction === 'rtl';
  const section = getCircleSection(startAngle, angleExtent, isRtl);
  const dimensionsRef = useRef(getDimensions(null, section));
  const circleWrapperRef = useRef<HTMLDivElement>(null);
  const [isDimsReady, setDimsReady] = useState<boolean>(false);

  useEffect(() => {
    if (props.onCommit || props.onInput || props.children) {
      dimensionsRef.current = getDimensions(circleWrapperRef.current, section);
      setDimsReady(true);
    }
  }, [size, section, props.onCommit, props.onInput, props.children]);

  const getValueFromEvent = (event: PointerEvent) => {
    const circleContainer = circleWrapperRef.current;
    if (event.target == circleContainer) {
      return getValue(
        event,
        min,
        max,
        step,
        dimensionsRef,
        startAngle,
        angleExtent,
        section,
        isRtl
      );
    }
    return;
  };

  const isPointerActiveRef = useRef(false);

  const pointerEventsProps = usePointerEvents(
    value,
    getValueFromEvent,
    circleWrapperRef,
    currentInputRef,
    isPointerActiveRef,
    props.onCommit,
    props.onInput
  );

  const keyboardEventsProps = useKeyboardEvents(
    value,
    min,
    max,
    step,
    currentInputRef,
    props.onCommit,
    props.onInput
  );

  const { datatipContent, datatipProps } = useMeterDatatip(
    value,
    isPointerActiveRef,
    props.datatip,
    props['aria-describedby']
  );

  const ariaProps = getMeterAriaProps(
    value,
    min,
    max,
    props['aria-label'],
    props['aria-labelledby'],
    props.thresholds
  );
  const mergedProps = mergeProps(pointerEventsProps, keyboardEventsProps, datatipProps, ariaProps);

  const { trackColor, indicatorColor } = getTrackAndIndicatorColor(
    value,
    thresholdDisplay,
    props.trackColor,
    props.indicatorColor,
    props.thresholds
  );

  const orientation =
    section === 'bottom' || section === 'top'
      ? 'Horizontal'
      : section === 'left' || section === 'right'
      ? 'Vertical'
      : 'Full';

  const meterInfo = {
    min,
    max,
    value,
    startAngle,
    angleExtent,
    size,
    section,
    isRtl,
    innerRadius: dimensionsRef.current.innerRadius
  } as MeterInfo;
  const isInteractive = props.onCommit || props.onInput;

  const { classes, styles } = useComponentTheme<MeterCircleVariantOptions, MeterCircleStyles>(
    MeterCircleRedwoodTheme,
    {
      size,
      interaction: isInteractive ? 'isInteractive' : undefined
    }
  );

  const { isTabbable } = useTabbableMode();
  const centerContext = getCenterContext(dimensionsRef, section);

  return (
    <>
      <div
        class={classNames([classes])}
        tabIndex={!isTabbable ? -1 : 0}
        {...testIdProps}
        {...mergedProps}>
        <CircleWrapper
          wrapperRef={circleWrapperRef}
          class={classNames([
            styles.circleWrapperBaseStyle,
            props.referenceLines && props.referenceLines.length > 0
              ? size && styles[`${size}${section}Style`]
              : '',
            styles[`meterCircle${orientation}SizeStyle`]
          ])}>
          <CircleInner
            class={classNames([styles.circleTrackBaseStyle, styles[`${section}Style`]])}
            {...getTrackProps(
              meterInfo,
              isTrackRendered,
              thresholdDisplay,
              trackColor,
              props.innerRadius,
              props.thresholds
            )}
          />
          {thresholdDisplay === 'all' &&
            props.thresholds &&
            getThresholdsProps(
              meterInfo,
              props.thresholds,
              isTrackRendered,
              trackColor,
              props.innerRadius
            ).map((threshold) => {
              return (
                <CircleInner
                  class={classNames([styles.circleTrackBaseStyle, styles[`${section}Style`]])}
                  {...threshold}
                />
              );
            })}

          <CircleInner
            color={indicatorColor}
            class={classNames([
              styles.circleValueBaseStyle,
              styles[`circleValue${section}Style`],
              styles[`${section}Style`]
            ])}
            {...getIndicatorProps(meterInfo, indicatorSize, props.innerRadius)}
          />
          {props.referenceLines?.map((refLine: CircularRefLine) => {
            return (
              <CircularReferenceLine
                {...getReferenceLinesProps(meterInfo, refLine, props.innerRadius)}
                section={section}
              />
            );
          })}
          {isDimsReady && props.children && centerContext && (
            <div class={classNames([styles.meterCircleCenterContent])}>
              {props.children(centerContext)}
            </div>
          )}
        </CircleWrapper>
      </div>
      {datatipContent}
    </>
  );
}
